import React from "react";
import WebProfitSpinner from "../../components/system/WebProfitSpinner";
import {
  Alert,
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  InputAdornment,
  MenuItem,
  Stack,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import PeopleAltRoundedIcon from "@mui/icons-material/PeopleAltRounded";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import BlockRoundedIcon from "@mui/icons-material/BlockRounded";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import RefreshRoundedIcon from "@mui/icons-material/RefreshRounded";
import ScheduleRoundedIcon from "@mui/icons-material/ScheduleRounded";
import VerifiedRoundedIcon from "@mui/icons-material/VerifiedRounded";
import PersonAddAlt1RoundedIcon from "@mui/icons-material/PersonAddAlt1Rounded";
import { useSearchParams } from "react-router-dom";
import { usersApi } from "../../api/users";

import EditorialIntro from "../../components/dashboard/EditorialIntro";
import ResponsiveListControls from "../../components/dashboard/ResponsiveListControls";
import ResponsiveOperationalTable from "../../components/dashboard/ResponsiveOperationalTable";
import DashboardLoadingState from "../../components/dashboard/DashboardLoadingState";
import { useListScrollRestoration, usePersistentListValue } from "../../utils/listViewState";
const BRAND = { orange: "#F35A3A", dark: "var(--wp-panel-text-primary, #19242F)" };

const STATUS_OPTIONS = [
  { value: "", label: "Todos los estados" },
  { value: "pending", label: "Pendientes" },
  { value: "active", label: "Activos" },
  { value: "suspended", label: "Suspendidos" },
  { value: "invited", label: "Invitados" },
];

const ORDER_OPTIONS = [
  { value: "asc", label: "Primero en llegar" },
  { value: "desc", label: "Últimos primero" },
];

const ROLE_LABELS = {
  superadmin: "Superadmin",
  staff: "Staff",
  client: "Cliente",
};

const DATE_FORMATTER = new Intl.DateTimeFormat("es-AR", {
  day: "2-digit",
  month: "short",
  year: "numeric",
  hour: "2-digit",
  minute: "2-digit",
});

function formatDate(value) {
  if (!value) return "Sin registro";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "Sin registro";
  return DATE_FORMATTER.format(date);
}

function initials(name = "", email = "") {
  const source = String(name || email || "U").trim();
  const parts = source.split(/\s+/).filter(Boolean);
  return parts.slice(0, 2).map((part) => part[0]?.toUpperCase()).join("") || "U";
}

function statusChip(status) {
  if (status === "active") return <Chip size="small" color="success" label="Activo" sx={{ fontWeight: 850 }} />;
  if (status === "pending") return <Chip size="small" color="warning" label="Pendiente" sx={{ fontWeight: 850 }} />;
  if (status === "suspended") return <Chip size="small" color="error" label="Suspendido" sx={{ fontWeight: 850 }} />;
  if (status === "invited") return <Chip size="small" color="info" label="Invitado" sx={{ fontWeight: 850 }} />;
  return <Chip size="small" label={status || "Sin estado"} sx={{ fontWeight: 850 }} />;
}

function Metric({ label, value, helper, icon, accent = BRAND.orange }) {
  return (
    <Box
      sx={{
        minWidth: { xs: "calc(50% - 8px)", md: 160 },
        flex: "1 1 160px",
        p: 2,
        borderRadius: 3,
        bgcolor: "var(--wp-panel-surface, #fff)",
        border: "1px solid rgba(var(--wp-panel-ink-rgb, 25,36,47),0.08)",
      }}
    >
      <Stack direction="row" spacing={1.25} alignItems="center">
        <Box
          sx={{
            width: 38,
            height: 38,
            borderRadius: 2.5,
            display: "grid",
            placeItems: "center",
            color: accent,
            bgcolor: `${accent}14`,
          }}
        >
          {icon}
        </Box>
        <Box>
          <Typography variant="caption" sx={{ color: "rgba(var(--wp-panel-ink-rgb, 25,36,47),.68)", fontWeight: 850 }}>
            {label}
          </Typography>
          <Typography variant="h5" sx={{ color: BRAND.dark, fontWeight: 950, lineHeight: 1.05 }}>
            {value}
          </Typography>
        </Box>
      </Stack>
      <Typography variant="caption" sx={{ display: "block", mt: 1, color: "rgba(var(--wp-panel-ink-rgb, 25,36,47),.68)" }}>
        {helper}
      </Typography>
    </Box>
  );
}

export default function UsersAdminPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const createdFrom = searchParams.get("createdFrom") || "";
  const createdTo = searchParams.get("createdTo") || "";
  const [status, setStatus] = usePersistentListValue("admin-users:status", "");
  const [order, setOrder] = usePersistentListValue("admin-users:order", "asc");
  const [search, setSearch] = usePersistentListValue("admin-users:search", "");
  const [debouncedSearch, setDebouncedSearch] = React.useState(() => search.trim());
  const [page, setPage] = usePersistentListValue("admin-users:page", 0);
  const [rowsPerPage, setRowsPerPage] = usePersistentListValue("admin-users:page-size", 25);
  const [users, setUsers] = React.useState([]);
  const [pagination, setPagination] = React.useState({ page: 1, limit: 25, total: 0, totalPages: 1 });
  const [summary, setSummary] = React.useState({ total: 0, active: 0, pending: 0, suspended: 0, invited: 0 });
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState("");
  const [savingId, setSavingId] = React.useState("");
  const requestSequence = React.useRef(0);

  React.useEffect(() => {
    const timer = window.setTimeout(() => setDebouncedSearch(search.trim()), 350);
    return () => window.clearTimeout(timer);
  }, [search]);

  const loadUsers = React.useCallback(async () => {
    const requestId = requestSequence.current + 1;
    requestSequence.current = requestId;

    try {
      setLoading(true);
      setError("");
      const data = await usersApi.list({
        status,
        search: debouncedSearch,
        order,
        page: page + 1,
        limit: rowsPerPage,
        createdFrom,
        createdTo,
      });

      if (requestId !== requestSequence.current) return;

      setUsers(Array.isArray(data.users) ? data.users : []);
      setPagination(data.pagination || { page: page + 1, limit: rowsPerPage, total: 0, totalPages: 1 });
      setSummary(data.summary || { total: 0, active: 0, pending: 0, suspended: 0, invited: 0 });
    } catch (err) {
      if (requestId !== requestSequence.current) return;
      setError(err.message || "No se pudieron cargar los usuarios");
    } finally {
      if (requestId === requestSequence.current) setLoading(false);
    }
  }, [status, debouncedSearch, order, page, rowsPerPage, createdFrom, createdTo]);

  React.useEffect(() => {
    loadUsers();
  }, [loadUsers]);

  useListScrollRestoration("admin-users", !loading);

  const approve = async (user, globalRole = "client") => {
    try {
      setSavingId(user.id);
      setError("");
      await usersApi.approve(user.id, { globalRole });
      await loadUsers();
    } catch (err) {
      setError(err.message || "No se pudo aprobar el usuario");
    } finally {
      setSavingId("");
    }
  };

  const suspend = async (user) => {
    const ok = window.confirm(`¿Suspender a ${user.email}? No podrá ingresar hasta que vuelvas a aprobarlo.`);
    if (!ok) return;

    try {
      setSavingId(user.id);
      setError("");
      await usersApi.suspend(user.id);
      await loadUsers();
    } catch (err) {
      setError(err.message || "No se pudo suspender el usuario");
    } finally {
      setSavingId("");
    }
  };

  const updateDateFilter = (key, value) => {
    const next = new URLSearchParams(searchParams);
    if (value) next.set(key, value); else next.delete(key);
    setSearchParams(next, { replace: true });
    setPage(0);
  };

  const arrivalNumber = (index) => {
    const offset = page * rowsPerPage;
    if (order === "asc") return offset + index + 1;
    return Math.max((pagination.total || 0) - offset - index, 1);
  };

  const arrivalTooltip = status || debouncedSearch
    ? "Posición dentro del filtro actual, respetando la fecha de llegada"
    : "Posición cronológica desde el primer registro";

  return (
    <Stack spacing={{ xs: 2, sm: 3 }} sx={{ minWidth: 0 }}>
      <EditorialIntro
        title="Usuarios"
        titleVariant="h3"
        context="Cuentas, roles y estado operativo."
        helpTitle="Qué podés revisar acá"
        helpText="El listado conserva el orden de registro e indica verificación, rol y estado para administrar accesos sin mezclarlo con la actividad de cada proyecto."
        action={
          <Button
            startIcon={loading ? <WebProfitSpinner size={16} color="inherit" /> : <RefreshRoundedIcon />}
            disabled={loading}
            onClick={loadUsers}
            variant="outlined"
            sx={{ borderRadius: 999, textTransform: "none", fontWeight: 900 }}
          >
            Actualizar
          </Button>
        }
      />

      <Stack direction="row" spacing={{ xs: 1, sm: 2 }} useFlexGap flexWrap="wrap">
        <Metric label="REGISTRADOS" value={summary.total || 0} helper="Total histórico" icon={<PeopleAltRoundedIcon fontSize="small" />} />
        <Metric label="ACTIVOS" value={summary.active || 0} helper="Pueden ingresar" icon={<CheckCircleRoundedIcon fontSize="small" />} accent="#2E7D32" />
        <Metric label="PENDIENTES" value={summary.pending || 0} helper="Esperando activación" icon={<ScheduleRoundedIcon fontSize="small" />} accent="#ED6C02" />
        <Metric label="SUSPENDIDOS" value={summary.suspended || 0} helper="Acceso bloqueado" icon={<BlockRoundedIcon fontSize="small" />} accent="#D32F2F" />
      </Stack>

      {error && <Alert severity="error">{error}</Alert>}

      <Card elevation={0} sx={{ borderRadius: 4, border: "1px solid rgba(var(--wp-panel-ink-rgb, 25,36,47),0.08)", overflow: "hidden" }}>
        <CardContent sx={{ p: { xs: 2, md: 2.5 }, borderBottom: "1px solid rgba(var(--wp-panel-ink-rgb, 25,36,47),0.08)" }}>
          <ResponsiveListControls
            search={
              <TextField
                value={search}
                onChange={(event) => { setSearch(event.target.value); setPage(0); }}
                placeholder="Buscar usuario"
                size="small"
                fullWidth
                inputProps={{ "aria-label": "Buscar por nombre o email" }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchRoundedIcon fontSize="small" />
                    </InputAdornment>
                  ),
                }}
                sx={{ bgcolor: "var(--wp-panel-surface, #fff)" }}
              />
            }
            activeFilterCount={[Boolean(status), order !== "asc", Boolean(createdFrom), Boolean(createdTo)].filter(Boolean).length}
            onClear={() => {
              setStatus("");
              setOrder("asc");
              const next = new URLSearchParams(searchParams);
              next.delete("createdFrom");
              next.delete("createdTo");
              setSearchParams(next, { replace: true });
              setPage(0);
            }}
            drawerTitle="Filtrar usuarios"
          >
            <TextField
              select
              size="small"
              label="Estado"
              value={status}
              onChange={(event) => { setStatus(event.target.value); setPage(0); }}
              sx={{ minWidth: { sm: 190 }, bgcolor: "var(--wp-panel-surface, #fff)" }}
            >
              {STATUS_OPTIONS.map((option) => (
                <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
              ))}
            </TextField>
            <TextField
              select
              size="small"
              label="Orden"
              value={order}
              onChange={(event) => { setOrder(event.target.value); setPage(0); }}
              sx={{ minWidth: { sm: 210 }, bgcolor: "var(--wp-panel-surface, #fff)" }}
            >
              {ORDER_OPTIONS.map((option) => (
                <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
              ))}
            </TextField>
            <TextField
              size="small"
              type="date"
              label="Alta desde"
              value={createdFrom}
              onChange={(event) => updateDateFilter("createdFrom", event.target.value)}
              InputLabelProps={{ shrink: true }}
              sx={{ minWidth: { sm: 170 }, bgcolor: "var(--wp-panel-surface, #fff)" }}
            />
            <TextField
              size="small"
              type="date"
              label="Alta hasta"
              value={createdTo}
              onChange={(event) => updateDateFilter("createdTo", event.target.value)}
              InputLabelProps={{ shrink: true }}
              sx={{ minWidth: { sm: 170 }, bgcolor: "var(--wp-panel-surface, #fff)" }}
            />
          </ResponsiveListControls>
        </CardContent>

        {loading && users.length ? <WebProfitSpinner size={24} sx={{ mx: "auto", my: 1 }} /> : null}

        {loading && !users.length ? (
          <DashboardLoadingState label="Cargando usuarios…" minHeight={320} />
        ) : (
        <TableContainer sx={{ overflow: "visible" }}>
          <ResponsiveOperationalTable mobileLabel="Usuarios" collapseAfter={3} sx={{ minWidth: 1120 }} aria-label="Tabla de usuarios por orden de llegada">
            <TableHead>
              <TableRow sx={{ bgcolor: "var(--wp-panel-surface-soft, #F7F8FB)" }}>
                <TableCell sx={{ fontWeight: 950, color: BRAND.dark, width: 105 }}>Llegada</TableCell>
                <TableCell sx={{ fontWeight: 950, color: BRAND.dark, minWidth: 260 }}>Usuario</TableCell>
                <TableCell sx={{ fontWeight: 950, color: BRAND.dark }}>Estado</TableCell>
                <TableCell sx={{ fontWeight: 950, color: BRAND.dark }}>Rol</TableCell>
                <TableCell sx={{ fontWeight: 950, color: BRAND.dark }}>Email</TableCell>
                <TableCell sx={{ fontWeight: 950, color: BRAND.dark, minWidth: 180 }}>Fecha de registro</TableCell>
                <TableCell align="right" sx={{ fontWeight: 950, color: BRAND.dark, minWidth: 280 }}>Acciones</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {!loading && users.length === 0 && (
                <TableRow>
                  <TableCell colSpan={7} sx={{ py: 8, textAlign: "center" }}>
                    <Box sx={{ width: 56, height: 56, mx: "auto", mb: 1.5, borderRadius: 3, display: "grid", placeItems: "center", bgcolor: "rgba(243,90,58,0.1)", color: BRAND.orange }}>
                      <PeopleAltRoundedIcon />
                    </Box>
                    <Typography sx={{ fontWeight: 950, color: BRAND.dark }}>No encontramos usuarios</Typography>
                    <Typography variant="body2" sx={{ color: "rgba(var(--wp-panel-ink-rgb, 25,36,47),.68)", mt: 0.5 }}>
                      Probá cambiando la búsqueda, el estado o el rango de alta.
                    </Typography>
                  </TableCell>
                </TableRow>
              )}

              {users.map((user, index) => {
                const protectedUser = user.isSuperAdmin || user.globalRole === "superadmin";
                const isSaving = savingId === user.id;
                const isActive = user.status === "active";

                return (
                  <TableRow
                    hover
                    key={user.id}
                    sx={{
                      "&:last-child td": { borderBottom: 0 },
                      opacity: isSaving ? 0.68 : 1,
                    }}
                  >
                    <TableCell>
                      <Tooltip title={arrivalTooltip}>
                        <Chip
                          icon={<PersonAddAlt1RoundedIcon />}
                          label={`#${String(arrivalNumber(index)).padStart(3, "0")}`}
                          size="small"
                          variant="outlined"
                          sx={{ fontWeight: 950, color: BRAND.dark, borderColor: "rgba(var(--wp-panel-ink-rgb, 25,36,47),0.16)" }}
                        />
                      </Tooltip>
                    </TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={1.5} alignItems="center">
                        <Avatar sx={{ width: 42, height: 42, bgcolor: "rgba(243,90,58,0.12)", color: BRAND.orange, fontWeight: 950 }}>
                          {initials(user.name, user.email)}
                        </Avatar>
                        <Box sx={{ minWidth: 0 }}>
                          <Stack direction="row" spacing={0.75} alignItems="center">
                            <Typography noWrap sx={{ maxWidth: 210, fontWeight: 950, color: BRAND.dark }}>
                              {user.name || "Sin nombre"}
                            </Typography>
                            {protectedUser && <VerifiedRoundedIcon sx={{ fontSize: 17, color: BRAND.orange }} />}
                          </Stack>
                          <Typography noWrap variant="body2" sx={{ maxWidth: 250, color: "rgba(var(--wp-panel-ink-rgb, 25,36,47),0.62)" }}>
                            {user.email}
                          </Typography>
                        </Box>
                      </Stack>
                    </TableCell>
                    <TableCell>{statusChip(user.status)}</TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        label={ROLE_LABELS[user.globalRole] || user.globalRole || "Cliente"}
                        variant="outlined"
                        sx={{ fontWeight: 850 }}
                      />
                    </TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        color={user.emailVerified ? "success" : "warning"}
                        variant="outlined"
                        label={user.emailVerified ? "Verificado" : "Sin verificar"}
                        sx={{ fontWeight: 850 }}
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 800, color: BRAND.dark }}>
                        {formatDate(user.createdAt)}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      {protectedUser ? (
                        <Chip size="small" label="Cuenta protegida" sx={{ fontWeight: 850 }} />
                      ) : (
                        <Stack direction={{ xs: "column", md: "row" }} spacing={1} justifyContent="flex-end" sx={{ width: { xs: "100%", md: "auto" }, "& .MuiButton-root": { minHeight: 40 } }}>
                          {!isActive && (
                            <Button
                              size="small"
                              disabled={isSaving}
                              onClick={() => approve(user, "client")}
                              variant="contained"
                              sx={{ bgcolor: BRAND.orange, borderRadius: 999, textTransform: "none", fontWeight: 900, whiteSpace: "nowrap" }}
                            >
                              {user.status === "suspended" ? "Reactivar cliente" : "Aprobar cliente"}
                            </Button>
                          )}
                          {!isActive && (
                            <Button
                              size="small"
                              disabled={isSaving}
                              onClick={() => approve(user, "staff")}
                              variant="outlined"
                              sx={{ borderRadius: 999, textTransform: "none", fontWeight: 900, whiteSpace: "nowrap" }}
                            >
                              {user.status === "suspended" ? "Como staff" : "Aprobar staff"}
                            </Button>
                          )}
                          {isActive && (
                            <Button
                              size="small"
                              startIcon={<BlockRoundedIcon />}
                              disabled={isSaving}
                              onClick={() => suspend(user)}
                              color="error"
                              variant="outlined"
                              sx={{ borderRadius: 999, textTransform: "none", fontWeight: 900 }}
                            >
                              Suspender
                            </Button>
                          )}
                        </Stack>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </ResponsiveOperationalTable>
        </TableContainer>
        )}

        <TablePagination
          component="div"
          count={pagination.total || 0}
          page={page}
          onPageChange={(_event, nextPage) => setPage(nextPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(event) => { setRowsPerPage(Number(event.target.value)); setPage(0); }}
          rowsPerPageOptions={[10, 25, 50, 100]}
          labelRowsPerPage="Por página"
          labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
          sx={{
            overflow: "hidden",
            "& .MuiTablePagination-toolbar": { px: { xs: 1, sm: 2 }, flexWrap: { xs: "wrap", sm: "nowrap" }, rowGap: .5 },
            "& .MuiTablePagination-spacer": { display: { xs: "none", sm: "block" } },
          }}
        />
      </Card>
    </Stack>
  );
}
